import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useGetSalesTeamPermissionMessages,
  useGetSalesTeamUsers,
} from '@user/store/salesTeam/hooks'
import { useFormikContext } from 'formik'
import { BulkReassignFormModel } from '../typings'
import { ENTITY_TYPES } from '@sales-center/common'
import { useGetOwnerList } from '@user/store/salesTeam/hooks/useGetOwnerList'
import {
  selectCurrentUser,
  useGetSalesTeamPositionsData,
} from '@user/store/authorization'
import { useBulkActionContext } from '../../../BulkActionContext'
import { ILead, selectAllLeads } from '@user/store/lead'
import { useSelector } from 'react-redux'
import { IOpportunity, selectAllOpportunities } from '@user/store/opportunity'
import { ISalesTeam } from '@user/store/salesTeam'

export const useFormFieldActions = (
  entityType: ENTITY_TYPES,
  salesTeams: ISalesTeam[],
) => {
  const {
    values: { salesTeamId, assigneeId, removeServices },
    setValues,
    isValidating,
    setFieldValue,
  } = useFormikContext<BulkReassignFormModel>()

  const { status: usersStatus, execute: getUsers } = useGetSalesTeamUsers()
  const { isAdmin } = useSelector(selectCurrentUser)

  const [closeOrDisqualifiedExists, setCloseOrDisqualifiedExists] =
    useState(false)
  const loadingUsers = useMemo(() => usersStatus === 'pending', [usersStatus])
  const ownerList = useGetOwnerList(
    ENTITY_TYPES.Lead,
    +salesTeamId,
    isAdmin && closeOrDisqualifiedExists,
  )
  const onChangeSalesTeam = useCallback(
    (value: ISalesTeam | null) => {
      if (value) {
        const salesTeamId = Number(value.id)
        setValues((v) => ({
          ...v,
          salesTeamId,
          assigneeId: '',
        }))
        getUsers(salesTeamId)
      }
      if (!value) {
        setValues((v) => ({
          ...v,
          salesTeamId: '',
          assigneeId: '',
        }))
      }
    },
    [getUsers, setValues],
  )

  const { selectedRows } = useBulkActionContext()

  const ids = useMemo(() => selectedRows.map((x) => Number(x)), [selectedRows])

  const leads = useSelector(selectAllLeads)
  const opportunities = useSelector(selectAllOpportunities)
  const entities: (ILead | IOpportunity)[] =
    entityType == ENTITY_TYPES.Lead ? leads : opportunities

  useEffect(() => {
    const filterSelectedRow = entities?.filter((row) =>
      selectedRows.includes(row.id),
    )
    if (filterSelectedRow?.length > 0) {
      const closedRecordsExists =
        filterSelectedRow.filter((row) => row.closedDate).length > 0
      setCloseOrDisqualifiedExists(closedRecordsExists)

      const salesTeamIds = filterSelectedRow?.map((row) => row.salesTeamId)
      const removeDuplicates = [...new Set(salesTeamIds)]
      if (removeDuplicates?.length == 1) {
        const salesTeamId = Number(removeDuplicates[0])
        setValues((v) => ({
          ...v,
          salesTeamId,
          assigneeId: '',
        }))
        getUsers(salesTeamId)
      }
      if (removeDuplicates?.length != 1) {
        setValues((v) => ({
          ...v,
          salesTeamId: '',
          assigneeId: '',
        }))
      }
    }
  }, [selectedRows, entities, setValues, getUsers])

  const salesTeamsBySelectedBranches = useMemo(() => {
    if (entityType == ENTITY_TYPES.Lead) {
      return salesTeams
    } else {
      const branchIds = new Set(
        entities.filter((x) => ids.includes(x.id)).map((x) => x.branchId),
      )
      return salesTeams.filter((x) => branchIds.has(x.branchId))
    }
  }, [entityType, entities, ids, salesTeams])

  const positionsDataStatus = useGetSalesTeamPositionsData()

  const { positionBasedAccessText, selectedSalesTeamAccessText } =
    useGetSalesTeamPermissionMessages(
      positionsDataStatus,
      salesTeamId,
      assigneeId,
    )

  const onChangeRemoveServices = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFieldValue('removeServices', checked)
    },
    [setFieldValue],
  )

  return {
    salesTeamId,
    removeServices,
    isValidating,
    loadingUsers,
    onChangeSalesTeam,
    ownerList,
    positionBasedAccessText,
    selectedSalesTeamAccessText,
    salesTeamsBySelectedBranches,
    onChangeRemoveServices,
    closeOrDisqualifiedExists,
  }
}
